import { useState, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Star } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'

type Warehouse = {
  id: number
  code: string
  name: string
  address: string
  city: string
  country_code: string
  is_active: boolean
  is_default: boolean
}

type Input = Omit<Warehouse, 'id'>

const emptyW: Input = {
  code: '', name: '', address: '', city: '', country_code: '',
  is_active: true, is_default: false,
}

export default function InventoryWarehouses() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: async () => (await api.get<Warehouse[]>('/inventory/warehouses/')).data,
  })

  const [editing, setEditing] = useState<Warehouse | null>(null)
  const [creating, setCreating] = useState(false)

  const createMut = useMutation({
    mutationFn: async (p: Input) => (await api.post('/inventory/warehouses/', p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['inventory', 'warehouses'] }); setCreating(false) },
  })
  const updateMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Input }) =>
      (await api.patch(`/inventory/warehouses/${id}/`, payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['inventory', 'warehouses'] }); setEditing(null) },
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/inventory/warehouses/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory', 'warehouses'] }),
  })

  const warehouses = data || []

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Warehouses</h2>
          <p className="text-sm text-gray-500">{warehouses.length} total</p>
        </div>
        <Button onClick={() => setCreating(true)}>+ New warehouse</Button>
      </div>

      {isLoading ? <p className="text-gray-500">Loading…</p> : warehouses.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No warehouses yet.</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>City</Th>
                <Th>Country</Th>
                <Th>Default</Th>
                <Th>Active</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {warehouses.map((w) => (
                <tr key={w.id} className="hover:bg-gray-50">
                  <Td className="font-mono">{w.code}</Td>
                  <Td>{w.name}</Td>
                  <Td>{w.city || '—'}</Td>
                  <Td className="font-mono text-xs">{w.country_code || '—'}</Td>
                  <Td>{w.is_default && <Star className="h-4 w-4 fill-green-600 text-green-600" />}</Td>
                  <Td><span className={`inline-block w-2 h-2 rounded-full ${w.is_active ? 'bg-green-500' : 'bg-gray-300'}`} /></Td>
                  <Td>
                    <button onClick={() => setEditing(w)} className="text-blue-600 hover:underline text-sm mr-3">Edit</button>
                    <button onClick={() => { if (confirm(`Delete ${w.code}?`)) deleteMut.mutate(w.id) }}
                      className="text-red-600 hover:underline text-sm">Delete</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <WarehouseDialog
          initial={editing ? { ...editing } : emptyW}
          isEdit={!!editing}
          submitting={createMut.isPending || updateMut.isPending}
          error={(createMut.error as any) || (updateMut.error as any)}
          onCancel={() => { setCreating(false); setEditing(null) }}
          onSubmit={(p) => {
            if (editing) updateMut.mutate({ id: editing.id, payload: p })
            else createMut.mutate(p)
          }}
        />
      )}
    </AdminLayout>
  )
}

function WarehouseDialog({
  initial, isEdit, onSubmit, onCancel, submitting, error,
}: {
  initial: Input
  isEdit: boolean
  onSubmit: (p: Input) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState<Input>(initial)
  const set = <K extends keyof Input>(k: K, v: Input[K]) => setForm((f) => ({ ...f, [k]: v }))
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(form) }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{isEdit ? 'Edit warehouse' : 'New warehouse'}</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldGroup>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="wh-code">Code</FieldLabel>
                  <Input
                    id="wh-code"
                    type="text"
                    required
                    value={form.code}
                    onChange={(e) => set('code', e.target.value.toUpperCase())}
                    className="font-mono"
                    placeholder="WH-MAIN"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="wh-country">Country (ISO 2)</FieldLabel>
                  <Input
                    id="wh-country"
                    type="text"
                    maxLength={2}
                    value={form.country_code}
                    onChange={(e) => set('country_code', e.target.value.toUpperCase())}
                    placeholder="RO"
                  />
                </Field>
              </div>
              <Field>
                <FieldLabel htmlFor="wh-name">Name</FieldLabel>
                <Input
                  id="wh-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Main Warehouse"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="wh-address">Address</FieldLabel>
                <Textarea
                  id="wh-address"
                  value={form.address}
                  onChange={(e) => set('address', e.target.value)}
                  rows={2}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="wh-city">City</FieldLabel>
                <Input
                  id="wh-city"
                  type="text"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                />
              </Field>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={(e) => set('is_default', e.target.checked)}
                />
                <span className="text-sm text-gray-700">Default warehouse (auto-selected for new movements)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => set('is_active', e.target.checked)}
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </FieldGroup>
          </FieldSet>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded mt-3">
              {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide ${className || ''}`}>{children}</th>
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm ${className || ''}`}>{children}</td>
}
